import type {EmailVerificationSendFailure} from "../auth/emailVerificationSend";
import {hasRecordedSendFor} from "./autoSendRegistry";

/**
 * The verify-email callout's send state, shared by every mounted instance (ui#1458).
 *
 * Plain TypeScript — no React — and a MODULE store rather than `useState`, because the callout has
 * two mount points that are alive at the same time: the files panel stays mounted behind the account
 * sheet (`HomePage`, ui#1061). With per-instance state, a first sign-in showed the panel's teal
 * "check your inbox" and, the moment the account sheet opened, a second instance rendering the blue
 * verify-your-address prompt with a Resend control — two contradictory answers on screen at once, and
 * a button that would spend the api's per-account budget on a mail sent seconds earlier.
 *
 * `useSyncExternalStore` is the React seam (`./useEmailVerificationPrompt`); the snapshot object is
 * replaced on every change and otherwise stable, which is what that hook requires.
 */
export interface VerificationSendState {
	/** The account this state belongs to. A different id resets everything. */
	readonly accountId: string | null;
	/** A mail has gone out for this account — this session, or a recorded earlier one. */
	readonly sent: boolean;
	/** A send is in flight. */
	readonly sending: boolean;
	/** The last send's failure, or null. */
	readonly failure: EmailVerificationSendFailure | null;
	/**
	 * The last send answered "this address is already confirmed" (ui#1477).
	 *
	 * Carried apart from {@link failure} because it is not one: nothing is wrong and there is nothing
	 * to retry. It still has to be SAID — a press that settles with no visible answer is a button that
	 * appears to do nothing, which is the same silent-nothing the failure line exists to prevent.
	 */
	readonly alreadyVerified: boolean;
	/** Whether a send was ever RECORDED for this account, which is what "resend" means. */
	readonly recorded: boolean;
}

const EMPTY: VerificationSendState = {
	accountId: null,
	sent: false,
	sending: false,
	failure: null,
	alreadyVerified: false,
	recorded: false,
};

let state: VerificationSendState = EMPTY;
const listeners = new Set<() => void>();

function publish(next: VerificationSendState): void {
	state = next;
	for (const listener of [...listeners]) {
		listener();
	}
}

/** The current snapshot. Stable between changes, as `useSyncExternalStore` requires. */
export function verificationSendState(): VerificationSendState {
	return state;
}

export function subscribeVerificationSend(listener: () => void): () => void {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
}

/**
 * Points the store at an account, reading its recorded history once.
 *
 * Called from the hook's effect rather than during render: the registry lives in `localStorage`, and
 * reading an external mutable store while rendering is exactly what React 19's concurrent rendering
 * does not promise to keep consistent between render and commit.
 */
export function focusVerificationSend(accountId: string | null): void {
	if (state.accountId === accountId) {
		return;
	}
	publish({...EMPTY, accountId, recorded: hasRecordedSendFor(accountId)});
}

/** A send has started. Optimistically `sent`, so the callout does not blink a control mid-flight. */
export function beginVerificationSend(accountId: string | null): void {
	publish({accountId, sent: true, sending: true, failure: null, alreadyVerified: false, recorded: true});
}

/** A send finished, one way or another. */
export function settleVerificationSend(
	accountId: string | null,
	outcome: "sent" | "alreadyVerified" | EmailVerificationSendFailure,
): void {
	if (state.accountId !== accountId) {
		// The account changed under an in-flight send; its result is no longer about anybody on screen.
		return;
	}
	publish({
		accountId,
		// `alreadyVerified` sent nothing, so it must not claim an inbox has mail waiting in it. It is
		// not a failure either — there is nothing for the user to fix — so it leaves the prompt with its
		// resend and says its own line instead of the failure one.
		sent: outcome === "sent",
		sending: false,
		failure: outcome === "sent" || outcome === "alreadyVerified" ? null : outcome,
		alreadyVerified: outcome === "alreadyVerified",
		recorded: state.recorded,
	});
}

/** Test seam: drops all state. Production never needs it — the store follows the account id. */
export function resetVerificationSendStore(): void {
	state = EMPTY;
	listeners.clear();
}
